import { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import type { DialogComponentProps } from '@gokrew/models/dialog';
import { TranslationNS } from '@gokrew/models/constants/translation-namespaces';
import { useDialog } from '@/context/DialogContext';
import { Button } from './Button';

type ConfirmationDialogProps = DialogComponentProps & {
  /** Keep the dialog open on failure and lock actions while the caller submits. */
  waitForSubmit?: boolean;
  variant?: 'default' | 'destructive' | 'warning';
  confirmLabel?: string;
  cancelLabel?: string;
  bullets?: string[];
  /** Keeps confirm disabled, e.g. until a typed confirmation matches. */
  confirmDisabled?: boolean;
};

/**
 * A confirmation dialog component that provides a cancel and confirm button.
 *
 * @param {ConfirmationDialogProps} props - The props for the confirmation dialog component.
 * @param {string} props.dialogId - The id of the dialog to close.
 * @param {Function} props.onSubmit - The function to call when the confirm button is clicked.
 * @param {Function} props.onClose - The function to call when the cancel button is clicked.
 * @param {React.ReactNode} props.children - The children of the confirmation dialog component.
 *
 * @returns The confirmation dialog component.
 */
export function ConfirmationDialog({
  dialogId,
  onSubmit,
  onClose,
  children,
  variant = 'default',
  confirmLabel,
  cancelLabel,
  bullets,
  waitForSubmit = false,
  confirmDisabled = false,
}: ConfirmationDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t } = useTranslation(TranslationNS.LibsCommon);
  const { closeDialog } = useDialog();
  const isDestructive = variant === 'destructive';
  const isWarning = variant === 'warning';
  const buttonLabel = confirmLabel ?? t('buttons.confirm');
  const cancelButtonLabel = cancelLabel ?? t('buttons.cancel');

  /**
   * Closes the dialog with the given dialogId.
   * This function is used to close the dialog after a successful submission or cancellation.
   */
  const closeDialogHandler = () => {
    if (dialogId) closeDialog(dialogId);
  };

  /**
   * Cancels the form submission and closes the dialog.
   * It calls the closeDialogHandler function to close the dialog with the given dialogId,
   * and if onClose is provided, it calls the onClose function to notify the parent component
   * that the dialog has been closed.
   */
  const cancelHandler = () => {
    try {
      closeDialogHandler();
      if (onClose) onClose();
    } catch (e) {
      console.error(e);
      toast(t('error.something_went_wrong'));
    }
  };

  /**
   * Confirms the form submission and closes the dialog.
   * It calls the onSubmit function if it is provided, and then calls the closeDialogHandler function
   * to close the dialog with the given dialogId.
   */
  const confirmHandler = async () => {
    if (isSubmitting || confirmDisabled) return;
    if (waitForSubmit) setIsSubmitting(true);
    try {
      if (waitForSubmit) await onSubmit?.();
      else onSubmit?.();
      closeDialogHandler();
    } catch (e) {
      if (!waitForSubmit) {
        console.error(e);
        toast(t('error.something_went_wrong'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      {bullets && bullets.length > 0 ? (
        <div className="space-y-3">
          <p className="text-sm font-semibold text-gray-800">
            {t('confirmation_dialog.assess_consequences')}
          </p>
          <ul className="space-y-2 pl-1">
            {bullets.map((bullet, i) => (
              <li
                key={i}
                className="flex items-start gap-2 text-sm text-gray-700"
              >
                <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-gray-500" />
                {bullet}
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="text-sm text-gray-600">{children}</div>
      )}

      {isDestructive && (
        <div className="flex items-start gap-2.5 rounded-lg border border-red-100 bg-red-50 p-3">
          <AlertTriangle
            size={16}
            className="mt-0.5 flex-shrink-0 text-red-500"
          />
          <p className="text-sm leading-relaxed text-gray-800">
            {t('confirmation_dialog.are_you_sure')}{' '}
            <span className="font-semibold text-red-600">
              {t('confirmation_dialog.cannot_be_undone')}
            </span>
          </p>
        </div>
      )}

      {/* action button container */}
      <div className="flex items-center gap-2 justify-end pt-1">
        <Button
          variant="outline"
          onClick={cancelHandler}
          disabled={isSubmitting}
        >
          {cancelButtonLabel}
        </Button>

        <Button
          variant={isDestructive || isWarning ? 'destructive' : 'primary'}
          onClick={confirmHandler}
          disabled={isSubmitting || confirmDisabled}
        >
          {buttonLabel}
        </Button>
      </div>
    </div>
  );
}
